import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "../../ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../ui/select";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import { Textarea } from "../../ui/textarea";
import {
    UserPlus,
    X,
    Monitor,
    Tag as TagIcon,
    AlertTriangle,
    CheckCircle,
    Users,
    Search,
    Shield
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { Role } from "./interface";
import { Device, Tag } from "../interfaces/EndpointInterfaces";
import { getTags, sentInvite } from "./Api/inviteuserdetails";
import useUserStore from "../../../services/userStore";

interface InviteUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    availableAdminRoles: Role[],
    availableUserRoles: Role[],
    availableMachines: Device[],
    tenantName: string,
    pendingUserCall: () => void,
    handleTab: (label: string) => void
}


export default function InviteUserDialog({ open, onOpenChange, availableAdminRoles, availableUserRoles, availableMachines, tenantName, pendingUserCall, handleTab }: InviteUserDialogProps) {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        type: "user" as "user" | "admin"
    });
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
    const [selectedMachines, setSelectedMachines] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [machineSearch, setMachineSearch] = useState("");
    const [roleSearch, setRoleSearch] = useState("");
    const [tagSearch, setTagSearch] = useState("");
    const [isTagSearchLoading, setIsTagSearchLoading] = useState(false);
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [errors, setErrors] = useState({ email: "" });
    const userData = useUserStore((state) => state.user);

    const [showMachineDropdown, setShowMachineDropdown] = useState(false);


    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        if (field === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!value.trim()) {
                setErrors((prev) => ({ ...prev, email: "Email is required" }));
            } else if (!emailRegex.test(value)) {
                setErrors((prev) => ({ ...prev, email: "Enter a valid email address" }));
            } else {
                setErrors((prev) => ({ ...prev, email: "" }));
            }
        }
    };

    const loadAvailableTags = async (search: string) => {
        if (!tenantName) return;
        setIsTagSearchLoading(true);
        try {
            const response = await getTags(tenantName, search);
            setAvailableTags(response);
        } catch (error) {
            console.error("Failed to fetch tags:", error);
            setAvailableTags([]);
        } finally {
            setIsTagSearchLoading(false);
        }
    };

    const handleTagSearch = async () => {
        await loadAvailableTags(tagSearch);
    };
    useEffect(() => {
        if (open) {
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                type: "user"
            });
            setSelectedRoles([]);
            setSelectedTags([]);
            setSelectedMachines([]);
            setMachineSearch("");
            setRoleSearch("");
            setTagSearch("");
            setShowMachineDropdown(false);
            loadAvailableTags("");
        }
    }, [open, tenantName]);
    const { toast } = useToast();



    const handleRoleSelect = (role: string) => {
        setSelectedRoles(prev => {
            if (formData.type === "admin") {
                return [role];
            }

            if (role === "epam-user") {
                return [role];
            } else {
                const filtered = prev.filter(r => r !== "epam-user");
                if (filtered.includes(role)) {
                    return filtered.filter(r => r !== role);
                } else {
                    return [...filtered, role];
                }
            }
        });
    };

    const handleTagSelect = (tagName: string) => {
        const tag = availableTags.find((item) => item.name === tagName);
        if (!tag) return;

        setSelectedTags((prev) => {
            const exists = prev.some((item) => item.id === tag.id);
            if (exists) {
                return prev.filter((item) => item.id !== tag.id);
            }
            return [...prev, tag];
        });
    };

    const handleMachineSelect = (machine: string) => {
        setSelectedMachines(prev => {
            if (prev.includes(machine)) {
                return prev.filter(m => m !== machine);
            } else {
                return [...prev, machine];
            }
        });
    };

    const validateForm = () => {
        if (!formData.firstName || !formData.lastName || !formData.email) {
            toast({
                title: "Missing Information",
                description: "Please fill in all required fields.",
                variant: "destructive",
            });
            return false;
        }

        if (selectedRoles.length === 0) {
            toast({
                title: "No Roles Selected",
                description: "Please select at least one role.",
                variant: "destructive",
            });
            return false;
        }

        if (formData.type === "user") {
            if (selectedRoles.includes("epam-user") && selectedMachines.length === 0) {
                toast({
                    title: "Machine Assignment Required",
                    description: "epam-user role requires at least one machine to be assigned.",
                    variant: "destructive",
                });
                return false;
            }
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast({
                title: "Invalid Email",
                description: "Please enter a valid email address.",
                variant: "destructive",
            });
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        const computerIdList = availableMachines
            .filter(machine => selectedMachines.includes(machine.dNSHostName ?? ""))
            .map(machine => machine.id)
            .filter(id => id !== undefined && id !== null);
        try {
            const payload = {
                tenantName: tenantName,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                userRoleList: selectedRoles
                    .map(roleName => {
                        const role = [...availableAdminRoles, ...availableUserRoles]
                            .find(r => r.roleName === roleName);
                        return role?.roleId;
                    })
                    .filter((id): id is number => !!id),
                computerIdList: computerIdList,
                roleType: formData.type,
                tags: selectedTags.map((tag) => tag.id).filter((id) => id !== undefined && id !== null),
            };

            let response = await sentInvite(payload);
            if (response?.isOk) {
                toast({
                    title: "Invitation Sent",
                    description: `Successfully sent invitation to ${formData.email}`,
                });
                pendingUserCall();
            } else {
                let errorMessage = response?.errorMessage;
                toast({
                    title: "Error",
                    description: `${errorMessage === "USER_EXISTS"
                        ? "User already exists"
                        : errorMessage === "INVITATION_EXISTS"
                            ? "An active invitation already exists for this email. Revoke it before sending a new one."
                            : errorMessage === "ADMINISTRATOR_EMAIL"
                                ? "This email belongs to a system administrator"
                                : errorMessage
                        }`,
                    variant: "destructive",
                });
            }

            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                type: "user"
            });
            setSelectedRoles([]);
            setSelectedTags([]);
            setSelectedMachines([]);
            setMachineSearch("");
            setShowMachineDropdown(false);
            handleTab(formData.type || "user")
        } catch (error) {
            console.error("Failed to send invite:", error);
            toast({
                title: "Error",
                description: "Failed to send invitation.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
            onOpenChange(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            onOpenChange(false);
        }
    };

    const hasEpamUser = selectedRoles.includes("epam-user");
    const canSelectOtherRoles = !hasEpamUser;
    const requiresMachine = hasEpamUser;

    const isFormValid = formData.firstName.trim() !== "" &&
        formData.lastName.trim() !== "" &&
        formData.email.trim() !== "" &&
        selectedRoles.length > 0;

    const currentRoles = formData.type === "admin" ? availableAdminRoles : availableUserRoles;

    const filteredMachines = availableMachines.filter(machine =>
        machine.dNSHostName?.toLowerCase().includes(machineSearch.toLowerCase())
    );


    const filteredRoles = currentRoles.filter((role) => {
        let typeMatch = false;

        if (formData.type === "user") {
            if (userData.userRole === "epam-admin") {
                typeMatch = role.roleName === "epam-user";
            } else if (userData.userRole === "server-pam-admin") {
                typeMatch =
                    role.roleType === "users" &&
                    role.roleName !== "epam-user" &&
                    role.roleName !== "super-user" &&
                    role.roleName !== "unassigned-user";
            } else {
                typeMatch =
                    role.roleType === "users" &&
                    role.roleName !== "super-user" &&
                    role.roleName !== "unassigned-user";
            }
        } else {
            typeMatch = role.roleType === "admin";
        }

        const searchMatch = role.roleName
            .toLowerCase()
            .includes(roleSearch.toLowerCase());

        return typeMatch && searchMatch;
    });

    const filteredTags = availableTags;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                            <UserPlus className="w-4 h-4 text-primary" />
                        </div>
                        User Invite
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3 p-4 overflow-y-auto flex-1 min-h-0">
                    {/* Basic Information */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input
                                id="firstName"
                                placeholder="Enter first name"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange("firstName", e.target.value)}
                                data-testid="input-first-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input
                                id="lastName"
                                placeholder="Enter last name"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange("lastName", e.target.value)}
                                data-testid="input-last-name"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            data-testid="input-email"
                        />
                        {errors.email && (
                            <p style={{ color: "red", fontSize: "0.85rem", marginTop: "4px" }}>
                                {errors.email}
                            </p>
                        )}
                    </div>

                    {/* User Type Selection */}
                    <div className="space-y-3">
                        <Label>User Type</Label>
                        <RadioGroup
                            value={formData.type}
                            onValueChange={(value) => {
                                handleInputChange("type", value);
                                // Clear selections when switching types
                                setSelectedRoles([]);
                                setSelectedTags([]);
                                setSelectedMachines([]);
                            }}
                            className="flex gap-6"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="user" id="user" data-testid="radio-user" />
                                <Label htmlFor="user" className="flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    User
                                </Label>
                            </div>
                            {userData.userRole !== "epam-admin" &&
                                userData.userRole !== "server-pam-admin" && (

                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="admin" id="admin" data-testid="radio-admin" />
                                        <Label htmlFor="admin" className="flex items-center gap-2">
                                            <Shield className="w-4 h-4" />
                                            Admin
                                        </Label>
                                    </div>)}
                        </RadioGroup>
                    </div>

                    {/* User Type Specific Fields */}
                    {formData.type === "user" && (
                        <div className="space-y-4">
                            {/* Role Selection */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label>Select Roles</Label>
                                    {hasEpamUser && (
                                        <div className="flex items-center gap-1 text-amber-600 text-xs">
                                            <AlertTriangle className="w-3 h-3" />
                                            epam-user cannot be combined with other roles
                                        </div>
                                    )}
                                </div>
                                <Card className="p-3">
                                    <div className="flex flex-wrap gap-2">
                                        {selectedRoles.map((role) => (
                                            <Badge
                                                key={role}
                                                variant="secondary"
                                                className="gap-1 pr-1"
                                                data-testid={`selected-role-${role}`}
                                            >
                                                {role}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                    onClick={() => handleRoleSelect(role)}
                                                >
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                        {selectedRoles.length === 0 && (
                                            <span className="text-muted-foreground text-sm">No roles selected</span>
                                        )}
                                    </div>
                                </Card>
                                <Select onValueChange={handleRoleSelect} data-testid="select-roles">
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose roles..." className="font-bold" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-80">
                                        {/* Search Input */}
                                        <div className="p-2 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                <Input
                                                    placeholder="Search roles..."
                                                    value={roleSearch}
                                                    onChange={(e) => setRoleSearch(e.target.value)}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    className="pl-8 h-8"
                                                    data-testid="input-search-roles"
                                                />
                                            </div>
                                        </div>

                                        {/* Role List */}
                                        <div className="max-h-60 overflow-y-auto">
                                            {filteredRoles.length > 0 ? (
                                                filteredRoles.map((role) => (
                                                    <SelectItem
                                                        key={role.roleName}
                                                        value={role.roleName}
                                                        disabled={!canSelectOtherRoles && role.roleName !== "epam-user"}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            {selectedRoles.includes(role.roleName) && <CheckCircle className="w-4 h-4 text-primary" />}
                                                            <Users className="w-4 h-4" />
                                                            {role.roleName}
                                                        </div>
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                <div className="p-2 text-center text-muted-foreground text-sm">
                                                    No roles found
                                                </div>
                                            )}
                                        </div>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Machine Selection */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label>Select Machines {requiresMachine && <span className="text-destructive">*</span>}</Label>
                                    {requiresMachine && (
                                        <div className="flex items-center gap-1 text-destructive text-xs">
                                            <AlertTriangle className="w-3 h-3" />
                                            Required for epam-user role
                                        </div>
                                    )}
                                </div>

                                {/* Selected Machines Display */}
                                <Card className="p-3">
                                    <div className="flex flex-wrap gap-2">
                                        {selectedMachines.map((machine) => (
                                            <Badge
                                                key={machine}
                                                variant="secondary"
                                                className="gap-1 pr-1"
                                                data-testid={`selected-machine-${machine}`}
                                            >
                                                <Monitor className="w-3 h-3" />
                                                {machine}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                    onClick={() => handleMachineSelect(machine)}
                                                    aria-label={`Remove ${machine} machine`}
                                                    data-testid={`remove-machine-${machine}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                        {selectedMachines.length === 0 && (
                                            <span className="text-muted-foreground text-sm">No machines selected</span>
                                        )}
                                    </div>
                                </Card>

                                {/* Machine Selection Dropdown */}
                                <Select
                                    onValueChange={handleMachineSelect}
                                    data-testid="select-machines"
                                    onOpenChange={setShowMachineDropdown}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose machines..." className="font-bold" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-80">
                                        {/* Search Input */}
                                        <div className="p-2 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                <Input
                                                    placeholder="Search machines..."
                                                    value={machineSearch}
                                                    onChange={(e) => setMachineSearch(e.target.value)}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    className="pl-8 h-8"
                                                    data-testid="input-search-machines"
                                                />
                                            </div>
                                        </div>

                                        {/* Machine List */}
                                        <div className="max-h-60 overflow-y-auto">
                                            {filteredMachines.length > 0 ? (
                                                filteredMachines.map((machine) => (
                                                    <SelectItem key={machine.dNSHostName} value={machine.dNSHostName || ""}>
                                                        <div className="flex items-center gap-2 py-1">
                                                            {selectedMachines.includes(machine.name) && <CheckCircle className="w-4 h-4 text-primary" />}
                                                            <Monitor className="w-4 h-4" />
                                                            <span>{machine.dNSHostName}</span>
                                                        </div>
                                                    </SelectItem>

                                                ))
                                            ) : (
                                                <div className="p-3 text-center text-muted-foreground text-sm">
                                                    No machines found
                                                </div>
                                            )}
                                        </div>
                                    </SelectContent>
                                </Select>

                                <div className="text-xs text-muted-foreground">
                                    {selectedMachines.length} of {availableMachines.length} machines selected
                                </div>
                            </div>
                        </div>
                    )}

                    {formData.type === "admin" && (
                        <div className="space-y-4">
                            {/* Admin Role Selection */}
                            <div className="space-y-3">
                                <Label>Select Admin Roles</Label>
                                <Card className="p-3">
                                    <div className="flex flex-wrap gap-2">
                                        {selectedRoles.map((role) => (
                                            <Badge
                                                key={role}
                                                variant="default"
                                                className="gap-1 pr-1"
                                                data-testid={`selected-role-${role}`}
                                            >
                                                {role}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                    onClick={() => handleRoleSelect(role)}
                                                    aria-label={`Remove ${role} role`}
                                                    data-testid={`remove-role-${role}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                        {selectedRoles.length === 0 && (
                                            <span className="text-muted-foreground text-sm">No roles selected</span>
                                        )}
                                    </div>
                                </Card>
                                <Select onValueChange={handleRoleSelect} data-testid="select-admin-roles">
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose admin roles..." className="font-bold" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-80">
                                        {/* Search Input */}
                                        <div className="p-2 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                <Input
                                                    placeholder="Search admin roles..."
                                                    value={roleSearch}
                                                    onChange={(e) => setRoleSearch(e.target.value)}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    className="pl-8 h-8"
                                                    data-testid="input-search-admin-roles"
                                                />
                                            </div>
                                        </div>

                                        {/* Admin Role List */}
                                        <div className="max-h-60 overflow-y-auto">
                                            {filteredRoles.length > 0 ? (
                                                filteredRoles.map((role) => (
                                                    <SelectItem key={role.roleName} value={role.roleName}>
                                                        <div className="flex items-center gap-2">
                                                            {selectedRoles.includes(role.roleName) && <CheckCircle className="w-4 h-4 text-primary" />}
                                                            <Shield className="w-4 h-4" />
                                                            {role.roleName}
                                                        </div>
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                <div className="p-2 text-center text-muted-foreground text-sm">
                                                    No admin roles found
                                                </div>
                                            )}
                                        </div>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Admin Tag Selection */}
                            <div className="space-y-3">
                                <Label>Select Tags (Optional)</Label>
                                <Card className="p-3">
                                    <div className="flex flex-wrap gap-2">
                                        {selectedTags.map((tag) => (
                                            <Badge
                                                key={tag.id}
                                                variant="secondary"
                                                className="gap-1 pr-1"
                                                data-testid={`selected-tag-${tag.name}`}
                                            >
                                                <TagIcon className="w-3 h-3" />
                                                {tag.name}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                    onClick={() => handleTagSelect(tag.name)}
                                                    aria-label={`Remove ${tag.name} tag`}
                                                    data-testid={`remove-tag-${tag.name}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                        {selectedTags.length === 0 && (
                                            <span className="text-muted-foreground text-sm">No tags selected</span>
                                        )}
                                    </div>
                                </Card>
                                <Select
                                    onValueChange={handleTagSelect}
                                    data-testid="select-tags"
                                    onOpenChange={(isOpen) => {
                                        if (isOpen) {
                                            loadAvailableTags(tagSearch);
                                        }
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose tags..." className="font-bold" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-80">
                                        {/* Search Input */}
                                        <div className="p-2 border-b">
                                            <div className="flex items-center gap-2">
                                                <div className="relative flex-1">
                                                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                    <Input
                                                        placeholder="Search tags..."
                                                        value={tagSearch}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setTagSearch(value);
                                                            if (!value.trim()) {
                                                                loadAvailableTags("");
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            e.stopPropagation();
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                handleTagSearch();
                                                            }
                                                        }}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        className="pl-8 h-8"
                                                        data-testid="input-search-tags"
                                                    />
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 shrink-0"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleTagSearch();
                                                    }}
                                                    disabled={isTagSearchLoading}
                                                    aria-label="Search tags"
                                                    data-testid="button-search-tags"
                                                >
                                                    <Search className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Tag List */}
                                        <div className="max-h-60 overflow-y-auto">
                                            {isTagSearchLoading ? (
                                                <div className="p-2 text-center text-muted-foreground text-sm">
                                                    Searching tags...
                                                </div>
                                            ) : filteredTags.length > 0 ? (
                                                filteredTags.map((tag) => (
                                                    <SelectItem key={tag.id} value={tag.name}>
                                                        <div className="flex items-center gap-2">
                                                            {selectedTags.some((selected) => selected.id === tag.id) && (
                                                                <CheckCircle className="w-4 h-4 text-primary" />
                                                            )}
                                                            <TagIcon className="w-3 h-3" />
                                                            {tag.name}
                                                        </div>
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                <div className="p-2 text-center text-muted-foreground text-sm">
                                                    No tags found
                                                </div>
                                            )}
                                        </div>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex-shrink-0">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        data-testid="button-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isFormValid}
                        className="gap-2"
                        data-testid="button-send-invite"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                Sending Invite...
                            </>
                        ) : (
                            <>
                                <UserPlus className="w-4 h-4" />
                                Send Invite
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}